import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from './AuthProvider';

const { FiAlertTriangle, FiRefreshCw, FiLogOut } = FiIcons;

const TenantErrorBoundary = ({ children }) => {
  const { tenantError, retryLoadTenant, signOut } = useAuth();

  if (!tenantError) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiAlertTriangle} className="text-2xl text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Connection Issue</h2>
              <p className="text-red-100 text-sm">Unable to load organization</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-4xl text-red-600" />
            </div>
            <p className="text-gray-700 text-lg mb-2 font-semibold">
              We couldn't load your organization
            </p>
            <p className="text-gray-600 text-sm">
              {tenantError}
            </p>
          </div>

          {/* Troubleshooting */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900 font-semibold mb-2">Try these steps:</p>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Check your internet connection</li>
              <li>Click "Retry" below</li>
              <li>Refresh your browser</li>
              <li>If the issue persists, try logging out and back in</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={retryLoadTenant}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Retry</span>
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all"
            >
              Refresh Page
            </button>
            <button
              onClick={signOut}
              className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiLogOut} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TenantErrorBoundary;
